import {
  ApplicationConfig,
  LOCALE_ID,
  inject,
  PLATFORM_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { DOCUMENT, isPlatformBrowser, registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeRu from '@angular/common/locales/ru';
import localeZh from '@angular/common/locales/zh';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ErrorStateMatcher, MAT_DATE_LOCALE, provideNativeDateAdapter, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideServiceWorker } from '@angular/service-worker';
import { authInterceptor } from './shared/store/auth/auth.interceptor';
import { StorageService } from './shared/services/storage.service';
import { API_BASE_URL } from './shared/api/api.config';
import { environment } from 'src/environments/environment';
import { AuthSessionService } from './shared/store/auth/auth-session.service';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { SplashManager } from './@core/splash-manager';
import { runBootstrapTasks } from './@core/bootstrap-initializer';
import { DataInitTask } from './@core/data-init.task';
import { InitTask } from './@core/init-task.interface';
import { UpdateInitTask } from './@core/update-init.task';
import { AppLanguageService } from './shared/i18n/app-language.service';
import { AppPaginatorIntl } from './shared/i18n/app-paginator-intl';
import { APP_TRANSLATE_LOADER_CONFIG } from './shared/i18n/app-translate-loader.config';
import { AppTranslateLoader } from './shared/i18n/app-translate-loader';
import {
  APP_INITIAL_LANGUAGE,
  APP_LANGUAGE_STORAGE_KEY,
  DEFAULT_APP_LANGUAGE,
  appLanguageToLocale,
  pickAppLanguage,
} from './shared/i18n/app-language.util';

registerLocaleData(localeRu);
registerLocaleData(localeEn);
registerLocaleData(localeZh);

function resolveInitialAppLocale(): string {
  const platformId = inject(PLATFORM_ID);
  const document = inject(DOCUMENT);
  const storage = inject(StorageService);
  const initialLanguage = inject(APP_INITIAL_LANGUAGE, { optional: true });

  const storedLanguage = isPlatformBrowser(platformId)
    ? storage.getItem(APP_LANGUAGE_STORAGE_KEY)
    : null;

  const browserLanguage = isPlatformBrowser(platformId) && typeof navigator !== 'undefined'
    ? navigator.language
    : null;

  return appLanguageToLocale(
    pickAppLanguage(
      initialLanguage,
      storedLanguage,
      document?.documentElement?.lang,
      browserLanguage,
      DEFAULT_APP_LANGUAGE,
    ),
  );
}

function shouldRunDataInitTask(): boolean {
  const document = inject(DOCUMENT);
  const pathname = document?.location?.pathname ?? '';
  const segments = pathname.split('/').filter(Boolean);

  return segments.includes('app');
}

async function unregisterDisabledServiceWorkers(platformId: object): Promise<void> {
  if (
    environment.enableServiceWorker
    || !isPlatformBrowser(platformId)
    || typeof navigator === 'undefined'
    || !navigator.serviceWorker?.getRegistrations
  ) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Stale service workers must not block app bootstrap in local/dev builds.
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: API_BASE_URL, useValue: environment.apiBaseUrl },
    {
      provide: LOCALE_ID,
      useFactory: resolveInitialAppLocale,
    },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: resolveInitialAppLocale,
    },
    {
      provide: MatPaginatorIntl,
      useClass: AppPaginatorIntl,
    },
    {
      provide: APP_TRANSLATE_LOADER_CONFIG,
      useValue: environment.i18nLoader,
    },
    provideTranslateService({
      lang: DEFAULT_APP_LANGUAGE,
      fallbackLang: DEFAULT_APP_LANGUAGE,
      loader: {
        provide: TranslateLoader,
        useClass: AppTranslateLoader,
      },
    }),
    provideAppInitializer(async () => {
      const storage = inject(StorageService);
      const platformId = inject(PLATFORM_ID);
      const languageSvc = inject(AppLanguageService);
      const authSession = inject(AuthSessionService);
      const translateSvc = inject(TranslateService);
      const updateInitTask = inject(UpdateInitTask);
      const dataInitTask = inject(DataInitTask);
      const shouldRunDataInit = shouldRunDataInitTask();

      storage.initialize();
      await unregisterDisabledServiceWorkers(platformId);
      await languageSvc.init();
      await authSession.init();
      const tasks: InitTask[] = [updateInitTask];

      if (shouldRunDataInit && authSession.getStatus() === 'authenticated') {
        tasks.unshift(dataInitTask);
      }

      await runBootstrapTasks(tasks, translateSvc);
      SplashManager.hide();
    }),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideHttpClient(
      withInterceptors([authInterceptor]),
    ),
    provideNativeDateAdapter(),
    provideRouter(routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      }),
      withComponentInputBinding()
    ),
    {
      provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.enableServiceWorker,
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ]
};
